/**
 * The push seam, browser half. See `transport.ts` for the shape and for why the native
 * one is not wired up yet.
 *
 * The browser needs less than the phone does and still cannot be finished here: Web
 * Push wants a service worker (this app HAS one — `web/sw.js`, registered for offline
 * and installability) and a **VAPID public key**, which is per-deployment and lives
 * with the server that signs the pushes. There is no such key in this repo and there
 * cannot be one — committing it would tie every deployment to one push origin.
 *
 * When the server grows a VAPID key, this becomes:
 *
 *   const reg = await navigator.serviceWorker.ready;
 *   const sub = await reg.pushManager.subscribe({
 *     userVisibleOnly: true,
 *     applicationServerKey: <VAPID public key>,
 *   });
 *   // `token` is then JSON.stringify(sub) — a Web Push subscription is an endpoint
 *   // plus two keys, not a bare string, and sv_notification's `web_push` provider is
 *   // what has to agree on that encoding.
 *
 * `PUSH_PLATFORM` is already right for that day, and it is the value the SERVICE
 * documents rather than the one the gateway's DTO comment suggests.
 */
import type {PushPlatform} from '@/entities/account/types';

export type PushMessagingApi = {
  requestPermission: () => Promise<boolean>;
  getToken: () => Promise<string>;
  onTokenRefresh: (cb: (token: string) => void) => () => void;
  onForegroundMessage: (cb: (message: PushMessage) => void) => () => void;
  onOpened: (cb: (message: PushMessage) => void) => () => void;
  getInitialOpened: () => Promise<PushMessage | null>;
};

export type PushMessage = {
  title: string | null;
  body: string | null;
  data: Record<string, string>;
};

export const PUSH_PLATFORM: PushPlatform = 'web_push';

export const PUSH_PROVIDER_NAME = 'Web Push';

export function pushMessaging(): PushMessagingApi | null {
  return null;
}
